'use client';

import {
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  type FC,
} from 'react';
import { timeZones } from '@/_constants/time-zones';
import {
  Alert,
  AssignFirmUserSelect,
  Button,
  ButtonGroup,
  Flex,
  Input,
  Select,
  Text,
  SelectItem,
  Separator,
  InputChips,
  FirmGroupSelector,
  Tooltip,
} from '@/_components';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { removeUserEmail, updateUser, UpdateUserAttrs } from '@/_api/users';
import { updateClientTags } from '@/_api/admin-tags';
import { ToastContext } from '@/_contexts/ToastProvider';
import { validateEmail } from '@/_utils/validations';
import { UserContext } from '@/_contexts/UserProvider';
import type { UserEditFormProps } from './UserEditForm.types';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getIsEmailTaken } from '@/_api/admin-users';
import { useDebounce } from '@/_hooks/useDebounce';
import styles from './UserEditForm.module.css';
import type { User } from '@/_types/models/user';
import { deformatPhoneNumber, formatPhoneNumber } from '@/_utils/phoneNumbers';
import { isTestEmail } from '@/_utils/meta';

const EMAIL_CHECK_DEBOUNCE = 200;

const CONTACT_METHOD_OPTIONS: Array<SelectItem> = [
  { name: 'Email & Text', value: 'sms' },
  { name: 'Email', value: 'email' },
];

export const UserEditForm: FC<UserEditFormProps> = ({
  setChangingEmail,
  onClose,
}) => {
  const [isLoading, setLoading] = useState<boolean>(false);

  const { token, user } = useContext(UserContext);
  const {
    selectedUser,
    addOrUpdateUser,
    refreshCompanyDetails,
    loadUsersForTable,
  } = useContext(CompanyContext);
  const { addToast } = useContext(ToastContext);
  const { firm, preferences, clientTagOptionsToAdd } =
    useContext(FirmSettingsContext);

  const [FName, setFName] = useState<string>('');
  const [LName, setLName] = useState<string>('');
  const [PName, setPName] = useState<string>('');
  const [chips, setChips] = useState<Set<string>>(new Set([]));
  const [chipText, setChipText] = useState<string>('');
  const [isEmailTaken, setIsEmailTaken] = useState<boolean>(false);
  const [takenUser, setTakenUser] = useState<User | undefined>(undefined);
  // const [isActive, setActive] = useState<boolean>(false);

  const onChipText = (event: React.ChangeEvent<HTMLInputElement>) =>
    setChipText(event.target.value);

  const _validateEmailAddress = useCallback(
    async (email?: string): Promise<{ isTaken: boolean; user?: User }> => {
      if (selectedUser?.email === email) return { isTaken: false };

      const isValidEmail: boolean = validateEmail(email);

      if (!isValidEmail) return { isTaken: false };

      try {
        const { is_available, user } = await getIsEmailTaken({
          token,
          email: email!,
        });

        return { isTaken: !is_available, user };
      } catch (error) {
        console.error(error);
        return { isTaken: false };
      }
    },
    [token, selectedUser?.email],
  );

  const validateEmailAddress = useCallback(
    async (email?: string) => {
      const { isTaken, user } = await _validateEmailAddress(email);

      setTakenUser(user);
      setIsEmailTaken(isTaken);
    },
    [_validateEmailAddress],
  );

  const onSave = async (
    user: Partial<UpdateUserAttrs>,
    skipClose = false,
  ): Promise<any> => {
    try {
      setLoading(true);

      const updatedUser = await updateUser({
        token,
        ...user,
        userId: selectedUser?.id!,
      });

      if (updatedUser?.error) {
        addToast('error', updatedUser.error, {
          error: updatedUser.error,
        });

        return { error: updatedUser.error };
      } else {
        addToast('success', 'The user was successfully updated');
      }

      updatedUser.contact_tags = Array.from(chips).map((name) => ({
        id: 0,
        name,
      }));

      await updateClientTags({
        token,
        user_id: selectedUser?.id!,
        contact_tags: Array.from(chips),
      });

      addOrUpdateUser(updatedUser);
      refreshCompanyDetails();

      if (!skipClose) {
        onClose();
      }
      return updatedUser;
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  const onFNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFName(event.target.value);
  };

  const onLNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLName(event.target.value);
  };

  const onPNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPName(event.target.value);

  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [externalUserId, setExternalUserId] = useState<string>('');
  const [firmGroupId, setFirmGroupId] = useState<string>('');

  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value?.trim()?.toLowerCase());
  const onPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPhone(event.target.value);
  const debouncedEmail = useDebounce(email, EMAIL_CHECK_DEBOUNCE);

  const [workPhone, setWorkPhone] = useState<string>('');
  const [workPhoneExt, setWorkPhoneExt] = useState<string>('');
  const [contactMethod, setContactMethod] = useState<string>(
    CONTACT_METHOD_OPTIONS[0]?.value || '',
  );

  const onWorkPhoneChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setWorkPhone(event.target.value);
  const onWorkPhoneExtChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setWorkPhoneExt(event.target.value);
  const onContactMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setContactMethod(event.target.value);
  const onExternalUserId = (event: React.ChangeEvent<HTMLInputElement>) =>
    setExternalUserId(event.target.value);
  const [timeZone, setTimeZone] = useState<string>('');

  const onFirmGroupIdChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newFirmGroupId = event.target.value;
    setFirmGroupId(newFirmGroupId);
  };

  const onTimeZoneChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setTimeZone(event.target.value);

  const handleRemoveEmail = async () => {
    if (!selectedUser) {
      return;
    }

    if (selectedUser.preferred_sign_in_method === 'email') {
      addToast(
        'error',
        'Cannot remove email when it is the preferred sign-in method',
      );
      return;
    }

    try {
      setLoading(true);
      const response = await removeUserEmail({
        userId: selectedUser.id,
        token,
      });
      if (!response.error) {
        setEmail('');
        refreshCompanyDetails();
        loadUsersForTable();
        onClose();
        addToast('success', 'Email successfully removed.');
      }
    } catch (error) {
      addToast('error', 'Unable to remove email, please try again later.', {
        error,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      setPhone(formatPhoneNumber(selectedUser?.phone_number || ''));
      setWorkPhone(formatPhoneNumber(selectedUser?.work_phone_number || ''));

      setFName(selectedUser?.fname || '');
      setLName(selectedUser?.lname || '');
      setPName(selectedUser?.preferred_name || '');
      // If temporary email, we have to hide it.
      setEmail(
        (selectedUser?.preferences?.has_temp_email
          ? ''
          : selectedUser?.email) || '',
      );
      setWorkPhoneExt(selectedUser?.work_phone_extension || '');
      setContactMethod(selectedUser?.preferred_contact_method || '');

      if (selectedUser?.time_zone) {
        setTimeZone(selectedUser.time_zone);
      } else if (firm?.time_zone) {
        setTimeZone(firm.time_zone);
      }

      setExternalUserId(selectedUser?.external_user_id || '');
      setChips(new Set(selectedUser?.contact_tags?.map((ct) => ct.name) || []));

      if (typeof selectedUser?.firm_group?.id === 'number') {
        setFirmGroupId(selectedUser?.firm_group?.id?.toString() || '');
      }
    }
  }, [firm?.time_zone, selectedUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isValidEmail: boolean = Boolean(
    // It's okay if the email is empty UNLESS the user had an email in the past. If so, it can't be removed now.
    (email && validateEmail(email)) ||
      (!email && !selectedUser?.email) ||
      (selectedUser?.preferences?.has_temp_email && email === ''),
  );

  // TODO: refactor once the notion of temporary emails is removed (SOR-15023)
  const hasRemovableEmail = Boolean(
    selectedUser?.email && !isTestEmail(selectedUser.email),
  );

  const canRemoveEmail = Boolean(
    hasRemovableEmail &&
      selectedUser?.preferred_sign_in_method !== 'email' &&
      !isLoading,
  );

  const isRemovingPhone = Boolean(
    deformatPhoneNumber(phone) === '' && !!selectedUser?.phone_number,
  );

  const canRemovePhone = Boolean(
    !isRemovingPhone || selectedUser?.preferred_sign_in_method !== 'sms',
  );

  const isFormValid: boolean =
    isValidEmail && !!FName && !!LName && !isEmailTaken && canRemovePhone;

  const handleSave = async () => {
    const isUpdatingEmail: boolean = email !== selectedUser?.email;

    try {
      if (!canRemovePhone) {
        addToast(
          'error',
          'Cannot remove phone number when SMS is the preferred sign-in method',
        );
        return;
      }

      const user: Partial<UpdateUserAttrs> = {
        email,
        fname: FName,
        lname: LName,
        phone_number: deformatPhoneNumber(phone),
        preferred_contact_method: contactMethod,
        external_user_id: externalUserId,
        preferred_name: PName,
        time_zone: timeZone,
        work_phone_extension: workPhoneExt,
        work_phone_number: deformatPhoneNumber(workPhone),
        group_id: parseInt(firmGroupId),
        assigned_advisor_id: selectedUser?.assigned_advisor_id,
      };

      const response = await onSave(user, isUpdatingEmail);

      if (response && !response.error && isUpdatingEmail) {
        setChangingEmail(email);
      } else if (response?.error) {
        addToast('error', response.error, { error: response.error });
      }
    } catch (error) {
      addToast('error', null, { error });
    }
  };

  const isEmailValid: boolean = useMemo(() => validateEmail(email), [email]);

  useEffect(() => {
    if (
      !!debouncedEmail &&
      !!selectedUser?.email &&
      debouncedEmail !== selectedUser?.email
    ) {
      validateEmailAddress(debouncedEmail);
    }
  }, [validateEmailAddress, debouncedEmail, selectedUser?.email]);

  return (
    <Flex isColumn={true} gap={10}>
      <Flex gap={6}>
        <Input
          label="First Name"
          value={FName}
          name="first-name"
          placeholder="John"
          isDisabled={isLoading}
          onChange={onFNameChange}
          isRequired={true}
        />
        <Input
          label="Last Name"
          value={LName}
          name="last-name"
          placeholder="Smith"
          isDisabled={isLoading}
          onChange={onLNameChange}
          isRequired={true}
        />
        <Input
          label="Preferred Name"
          name="preferred-name"
          isDisabled={isLoading}
          value={PName}
          placeholder="Johnny"
          onChange={onPNameChange}
        />
      </Flex>
      <Flex isColumn={true} gap={4}>
        <Flex gap={6}>
          <Flex gap={2} isColumn={true}>
            <Text type="small" fontWeight="medium">
              Email
            </Text>
            <Flex gap={4} isYCentered>
              <Input
                label="Email"
                className={styles.userEditForm__email}
                name="email"
                value={email}
                isLabelHidden={true}
                placeholder="john.doe@example.com"
                onChange={onEmailChange}
                isDisabled={isLoading}
                iconRight={
                  !isEmailTaken && isEmailValid ? 'check-circle' : undefined
                }
                iconRightColor="success"
                helperText={
                  isEmailValid && isEmailTaken
                    ? 'This email already exists'
                    : ''
                }
                helperTextColor="critical"
              />
              <Tooltip placement="top" isDisabled={canRemoveEmail}>
                <Tooltip.Trigger>
                  <Button
                    value="Remove email"
                    intent="danger-secondary"
                    onClick={handleRemoveEmail}
                    isIconOnly={true}
                    iconLeft="trash"
                    isDisabled={!canRemoveEmail}
                    withTooltip={canRemoveEmail}
                    tooltip={canRemoveEmail ? 'Remove email' : undefined}
                  />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  <Text type="small" color="currentcolor">
                    {!hasRemovableEmail
                      ? 'No email to remove'
                      : selectedUser?.preferred_sign_in_method === 'email'
                        ? 'Email is your preferred sign-in method and cannot be removed'
                        : 'Remove email'}
                  </Text>
                </Tooltip.Content>
              </Tooltip>
            </Flex>
          </Flex>
          <Input
            label="Phone Number"
            placeholder="(000) 000-0000"
            value={phone}
            name="phone-number"
            onChange={onPhoneChange}
            isDisabled={isLoading}
            mask="(___) ___-____"
          />
          {preferences?.external_user_id_allowed && (
            <Input
              label="External User ID"
              value={externalUserId}
              placeholder="JSMITH123"
              isDisabled={isLoading}
              onChange={onExternalUserId}
            />
          )}
        </Flex>
        {isEmailTaken && takenUser?.id && (
          <Alert type="danger">
            This email address is already linked to another user,{' '}
            <b>
              {takenUser?.fname}
              {takenUser?.lname && ` ${takenUser?.lname}`}
            </b>
            . Please try a unique email address or change {takenUser?.fname}
            &apos;s email address first.
          </Alert>
        )}
        {!canRemovePhone && (
          <Alert type="danger">
            Cannot remove phone number when SMS is the preferred sign-in method
          </Alert>
        )}
      </Flex>
      <Separator />
      <Flex gap={6}>
        <Input
          label="Work Phone"
          mask="(___) ___-____"
          placeholder="(000) 000-0000"
          value={workPhone}
          isDisabled={isLoading}
          onChange={onWorkPhoneChange}
        />
        <Input
          label="Ext"
          placeholder="e.g., 3"
          value={workPhoneExt}
          isDisabled={isLoading}
          onChange={onWorkPhoneExtChange}
        />
        <Select
          items={CONTACT_METHOD_OPTIONS}
          label="Contact Method"
          isDisabled={isLoading}
          onChange={onContactMethodChange}
          selectedValue={contactMethod}
        />
      </Flex>
      <Flex gap={6}>
        <InputChips
          results={clientTagOptionsToAdd}
          chips={chips}
          label="User tag"
          onChange={onChipText}
          setChips={setChips}
          value={chipText}
          placeholder="Select or Add Tags"
        />
        <Select
          items={timeZones.map((tz) => ({ name: tz, value: tz }))}
          label="Timezone"
          isDisabled={isLoading}
          onChange={onTimeZoneChange}
          selectedValue={timeZone}
        />
      </Flex>
      <Flex gap={4}>
        {preferences.has_firm_groups && (
          <FirmGroupSelector
            value={firmGroupId}
            onChange={onFirmGroupIdChange}
            isDisabled={user?.firm_access === 'group' || isLoading}
          />
        )}
        <AssignFirmUserSelect
          isLoading={isLoading}
          userId={selectedUser?.id!}
          selectedAdvisorId={selectedUser?.assigned_advisor_id}
          selectedFirmGroupId={parseInt(firmGroupId)}
        />
      </Flex>
      <ButtonGroup align="right">
        <Button value="Cancel" onClick={onClose} intent="secondary" />
        <Button
          value="Save"
          onClick={handleSave}
          intent="primary"
          isDisabled={!isFormValid || isLoading}
        />
      </ButtonGroup>
    </Flex>
  );
};
